import React, { useEffect, useState } from 'react';
import styles from './OrganizationCard.module.scss';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CircleIcon from '@mui/icons-material/Circle';
import EditIcon from '@mui/icons-material/Edit';
import { OrganizationDto } from '../../../api/types/dtos';
import { InspectionDto } from '../../../api/types/dtos/inspection';
import { useAuthorization } from '../../../contexts/AuthorizationContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import clsx from 'clsx';
import CaravelaLogo from '../../../assets/CaravelaLogo.png';

interface OrganizationCardProps {
  organization: OrganizationDto;
  selected?: boolean;
  onSelect?: (id: string) => void;
  onClick?: (inspection?: InspectionDto) => void;
  onEdit?: (organization: OrganizationDto) => void;
  inspection?: InspectionDto;
}

const OrganizationCard: React.FC<OrganizationCardProps> = ({ 
  organization, 
  selected = false,
  onSelect,
  onClick,
  onEdit,
  inspection
}) => {
  // Get the first letter for the avatar or use a fallback
  const getAvatarContent = () => {
    if (!organization?.name) {
      return <BusinessIcon />;
    }
    return organization.name.charAt(0).toUpperCase();
  };

  // Get the selected organization from context (the main organization the user is viewing)
  const { selectedOrganization: mainOrganization } = useOrganization();

  // Authorization check for edit button
  const { hasPrivilege, loading: authLoading } = useAuthorization();
  const canEdit = hasPrivilege('ADMIN_ACCESS') || hasPrivilege('SUPER_ADMIN');
  const [showEditButton, setShowEditButton] = useState(false);

  // Effect to show edit button with delay to prevent loading flicker
  useEffect(() => {
    if (!authLoading && canEdit) {
      const timer = setTimeout(() => {
        setShowEditButton(true);
      }, 1000); // 1 second delay
      return () => clearTimeout(timer);
    }
  }, [authLoading, canEdit]);

  const handleSelect = () => {
    if (onSelect && organization.id) {
      onSelect(organization.id);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // If clicking the checkbox or edit button, don't trigger the card click
    if ((e.target as HTMLElement).closest('.MuiCheckbox-root') || 
        (e.target as HTMLElement).closest('.edit-button')) {
      return;
    }
    onClick?.(inspection);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(organization);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Card 
      className={clsx(
        styles.card, 
        selected && styles.selected,
        inspection && styles.hasInspection
      )}
      onClick={handleClick}
      sx={{ cursor: 'pointer' }}
    >
      {/* Checkbox - only show if onSelect is provided */}
      {onSelect && (
        <div 
          className={clsx(styles.checkbox, selected && styles.selected)}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onChange={handleSelect}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Edit Button - only show if user has permissions */}
      {showEditButton && onEdit && (
        <div className={styles.editButtonContainer}>
          <Button
            className="edit-button"
            variant="outlined"
            size="small"
            onClick={handleEditClick}
            sx={{ 
              borderColor: 'transparent',
              color: '#1976d2',
              minWidth: 'auto',
              width: '100%',
              height: '100%',
              padding: '0',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderColor: '#1976d2',
              }
            }}
          >
            <EditIcon fontSize="medium" />
          </Button>
        </div>
      )}

      {/* Logo/Image */}
      <div className={styles.imageContainer}>
        {organization?.organizationLogo ? (
          <img 
            src={organization?.organizationLogo || "logo"} 
            alt={`${organization.name || 'Organization'} logo`}
            className={styles.image}
          />
        ) : (mainOrganization?.id === '17ce2b3c-d4e1-47a5-b407-6206a0cf6663' || mainOrganization?.id === '42122b14-3e80-426d-94ca-529cef963021') ? (
          <img 
            src={CaravelaLogo} 
            alt="Caravela Better Coffee logo"
            className={styles.logoImage}
          />
        ) : (
          <Avatar className={styles.avatar}>
            {getAvatarContent()}
          </Avatar>
        )}
      </div>

      <CardContent className={styles.content}>
        {/* Organization Name */}
        <h2 className={styles.title}>
          {organization?.name || 'Unnamed Organization'}
        </h2>

        {/* Admin User Name */}
        <div className={styles.adminInfo}>
          <PersonIcon fontSize="small" />
          <span>
            {organization?.adminUserName || 'No farmer assigned'}
          </span>
        </div>

        {/* Inspection Info - Only show if inspection prop is provided */}
        {inspection && (
          <>
            <div className={styles.inspectionInfo}>
              <CalendarTodayIcon fontSize="small" />
              <span>Start: {formatDate(inspection.startDate)}</span>
            </div>
            <div className={styles.statusInfo}>
              <CircleIcon 
                fontSize="small" 
                className={clsx(
                  styles.statusIcon,
                  inspection.closeDate ? styles.statusClosed : styles.statusOpen
                )}
              />
              <span>{inspection.closeDate ? 'Closed' : 'Open'}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationCard; 